import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tag, Upload, Button, Tooltip, App } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { idiomService, contentCategoryService } from '../../../services/cms/contentManagementService';

const { Text } = Typography;
const STATUS_OPTIONS = [{ label: 'Active', value: 1 }, { label: 'Đợi duyệt', value: 2 }];

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  // Bảng idiom có cả title lẫn content (giống CMS cũ): dòng import Excel luôn có title = 'ip',
  // nội dung danh ngôn thật nằm ở cột content -> phải hiện cả hai cột mới đọc được dữ liệu import.
  { title: 'TIÊU ĐỀ', dataIndex: 'title', key: 'title', width: 160, ellipsis: true },
  { title: 'DANH NGÔN', dataIndex: 'content', key: 'content', render: (t) => <Text strong>{t}</Text>, ellipsis: true },
  { title: 'TÁC GIẢ', dataIndex: 'author', key: 'author', width: 150 },
  { title: 'NGƯỜI TẠO', dataIndex: 'author_name', key: 'author_name', width: 140, render: (val, r) => val || r.create_by || r.created_by || '-' },
  { title: 'DANH MỤC', dataIndex: 'category_id', key: 'category_id', width: 150, render: (val, r) => r.category_name ? `[${val}] ${r.category_name}` : (val || '-') },
  { title: 'ẢNH', dataIndex: 'image_thumb', key: 'image_thumb', width: 80, render: (u) => u ? <img src={u} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-' },
  { title: 'VIEWS', dataIndex: 'total_view', key: 'total_view', width: 80 },
  { title: 'TRẠNG THÁI', dataIndex: 'status', key: 'status', width: 100, render: (s) => <Tag color={s === 1 ? 'green' : 'orange'}>{s === 1 ? 'ACTIVE' : 'ĐỢI DUYỆT'}</Tag> },
  { title: 'NGÀY TẠO', dataIndex: 'create_time', key: 'create_time', width: 140, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
];

const QuanLyDanhNgon = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [categories, setCategories] = useState([]);

  // Import danh ngôn từ Excel (rule engine ở backend, giống lichviet_cms)
  const importMutation = useMutation({
    mutationFn: idiomService.importExcel,
    onSuccess: (res) => {
      const imported = res?.data?.imported ?? 0;
      message.success(res?.message || `Import thành công ${imported} danh ngôn`);
      queryClient.invalidateQueries({ queryKey: ['idiomList'] });
    },
    onError: (error) => {
      // Interceptor axios đã tự hiện thông báo cho lỗi 400; fallback cho trường hợp khác
      const msg = error?.data?.message || error?.message;
      if (msg) message.error(msg);
    },
  });

  const handleBeforeUpload = (file) => {
    const isValid = /\.(xlsx|csv)$/i.test(file.name);
    if (!isValid) {
      message.error('Chỉ chấp nhận file .xlsx hoặc .csv (file .xls hãy lưu lại thành .xlsx)');
      return Upload.LIST_IGNORE;
    }
    importMutation.mutate(file);
    return false; // chặn upload mặc định của antd
  };

  const headerActions = (
    <Tooltip title="File Excel (dòng 1 là header, không import): A = STT, B = tên rule (ghi 'quote'), C = ID danh mục, D = nội dung danh ngôn, E = tác giả, F = tiêu đề. Tất cả cột C-F đều bắt buộc.">
      <Upload accept=".xlsx,.csv" showUploadList={false} beforeUpload={handleBeforeUpload} maxCount={1}>
        <Button icon={<UploadOutlined />} loading={importMutation.isPending}>
          Import from Excel
        </Button>
      </Upload>
    </Tooltip>
  );

  useEffect(() => {
    contentCategoryService.listActive().then((res) => {
      if (Array.isArray(res)) {
        setCategories(res.map((c) => ({ label: `[${c.id}] ${c.title}`, value: c.id })));
      }
    });
  }, []);

  const formFields = useMemo(() => [
    { name: 'title', label: 'Tiêu đề', type: 'input', rules: [{ required: true, message: 'Vui lòng nhập!' }] },
    { name: 'author', label: 'Tác giả', type: 'input' },
    { name: 'category_id', label: 'Danh mục', type: 'select', options: categories, propsInput: { showSearch: true } },
    { name: 'content', label: 'Danh ngôn (nội dung)', type: 'textarea', propsInput: { rows: 4 } },
    { name: 'image_thumb', label: 'Ảnh', type: 'upload-cdn' },
    { name: 'metadata', label: 'Metadata', type: 'textarea', propsInput: { rows: 2 } },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1 },
  ], [categories]);

  const filterFields = useMemo(() => [
    {
      label: 'Chọn danh mục',
      value: 'category_id',
      options: [{ label: 'Tất cả danh mục', value: '' }, ...categories],
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả trạng thái', value: '' },
        ...STATUS_OPTIONS,
      ],
    },
  ], [categories]);

  return (
    <CrudPage
      title="QUẢN LÝ DANH NGÔN"
      subTitle="Quản lý danh ngôn trong hệ thống"
      itemsName="Danh ngôn"
      queryKey="idiomList"
      service={idiomService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm..."
      addButtonLabel="Thêm danh ngôn"
      renderHeaderActions={headerActions}
    />
  );
};

export default QuanLyDanhNgon;
